import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { callParkingBoxApi } from "../lib/parkingBoxApi";
import { useAuth } from "../hooks/useAuth";
import { normalizeAuthApiResponse } from "../lib/authResponse";
import { persistAuthCredentials } from "../lib/authCredentials";

const inputClasses = [
  "w-full rounded-xl border px-4 py-3 text-sm transition",
  "border-slate-300 bg-white text-slate-900 placeholder:text-slate-500",
  "focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-300/60",
  "dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-300/70",
  "dark:focus:border-purple-400 dark:focus:ring-purple-500/40",
].join(" ");

type Feedback =
  | {
      type: "success" | "error";
      message: string;
    }
  | null;

export default function RegisterPage() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const routeError = useMemo(() => searchParams.get("error") ?? null, [searchParams]);

  useEffect(() => {
    if (routeError) {
      setFeedback({
        type: "error",
        message: routeError,
      });
    }
  }, [routeError]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const formData = new FormData(formElement);
    const email = String(formData.get("email") ?? "").trim();
    const password = String(formData.get("password") ?? "");
    const confirmPassword = String(formData.get("confirmPassword") ?? "");

    if (!email || !password || !confirmPassword) {
      setFeedback({
        type: "error",
        message: "Wypełnij wszystkie pola formularza.",
      });
      return;
    }

    if (password !== confirmPassword) {
      setFeedback({
        type: "error",
        message: "Hasła muszą być identyczne.",
      });
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    try {
      const registrationResponse = await callParkingBoxApi({
        action: "add-user",
        username: email,
        password,
      });

      const registrationPayload = (registrationResponse.json ?? null) as Record<string, unknown> | null;
      const registrationStatus =
        registrationPayload && typeof registrationPayload.status === "number" ? registrationPayload.status : null;
      if (registrationStatus !== 1) {
        throw new Error("Nie otrzymano potwierdzenia utworzenia konta (status !== 1).");
      }

      const loginResponse = await callParkingBoxApi({
        action: "login",
        username: email,
        password,
      });

      const normalizedLogin = normalizeAuthApiResponse(loginResponse.json);
      const jwtToken = normalizedLogin?.token ?? null;
      const loginStatus = normalizedLogin?.status ?? null;
      const permissionLevel = normalizedLogin?.permission ?? null;
      const userId = normalizedLogin?.userId ?? null;

      if (!jwtToken || loginStatus !== 1) {
        throw new Error("Konto utworzono, ale nie udało się automatycznie zalogować.");
      }

      login(jwtToken, { remember: true, userEmail: email, userId, permission: permissionLevel });
      persistAuthCredentials({ username: email, password, remember: true });
      formElement.reset();
      navigate("/", { replace: true });
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : "Błąd podczas łączenia z API rejestracji.";
      setFeedback({
        type: "error",
        message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900 transition-colors dark:bg-neutral-950 dark:text-slate-100">
      <div className="mx-auto flex w-full max-w-xl flex-col gap-10">
        <div className="flex justify-center text-sm">
          <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs uppercase tracking-[0.2em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            Parking Box
          </span>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-10 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.15)] transition-colors dark:border-white/10 dark:bg-neutral-900/80">
          <div className="mb-8 space-y-3 text-center">
            <h1 className="text-3xl font-semibold tracking-tight">Rejestracja</h1>
          </div>

          {feedback ? (
            <div
              className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
                feedback.type === "success"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200"
                  : "border-red-200 bg-red-50 text-red-600 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200"
              }`}
            >
              {feedback.message}
            </div>
          ) : null}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Adres e-mail
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={inputClasses}
                placeholder="nazwa@mail.com"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Hasło
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className={inputClasses}
                placeholder="********"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Potwierdź hasło
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className={inputClasses}
                placeholder="********"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-xl bg-purple-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-purple-400/30 transition hover:bg-purple-500 disabled:opacity-70 dark:bg-purple-500 dark:hover:bg-purple-400"
            >
              {isSubmitting ? "Zapisywanie..." : "Zarejestruj się"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-300/80">
            Masz już konto?{" "}
            <Link to="/login" className="font-semibold text-purple-600 transition hover:text-purple-500 dark:text-purple-400 dark:hover:text-purple-300">
              Zaloguj się
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
